import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Disc3, SlidersHorizontal, Speaker, Download, Zap, Radio, Cable } from "lucide-react";
export default function PerformanceArsenal() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const equipment = [{
    title: "Flagship Nexus",
    model: "CDJ-3000 + DJM-A9",
    description: "The industry-standard club layout. Uncompromised audio manipulation with the A9's 32-bit A/D converter and the CDJ-3000's MPU processing power. Designed for precision mixing and harmonic dominance.",
    specs: ["32-bit D/A Converter", "Gigabit Ethernet", "Magvel Fader Gen 2"],
    icon: <Disc3 className="w-12 h-12 text-white" />,
    image: "https://djtaurx.com/wp-content/uploads/2026/02/SJP03892-scaled.jpg?v=xf4r2lgr28h"
  }, {
    title: "All-In-One Command",
    model: "XDJ-RX2 Console",
    description: "A standalone performance beast. Features a 7-inch touch screen and layout inherited from the NXS2 series. Portable tactical unit for rapid deployment without sacrificing professional workflow.",
    specs: ["7-inch Touch Screen", "16 Performance Pads", "Dual USB Link"],
    icon: <SlidersHorizontal className="w-12 h-12 text-white" />,
    image: "https://djtaurx.com/wp-content/uploads/2026/02/SJP03503-1-scaled.jpg?v=z4mwfye2bom"
  }, {
    title: "Sonic Architecture",
    model: "AZ Sound System",
    description: "High-fidelity audio reinforcement. The AZ setup delivers crystal-clear frequencies and earth-shaking bass response, ensuring the 'Born to Dominate' sonic signature reaches every corner of the venue.",
    specs: ["Active Crossover", "High SPL Output", "Zero Distortion"],
    icon: <Speaker className="w-12 h-12 text-white" />,
    image: "https://djtaurx.com/wp-content/uploads/2026/02/WhatsApp-Image-2026-02-25-at-20.40.56.jpeg?v=sv4iwlkzym"
  }];
  return <section id="performance-arsenal" className="dark bg-background text-foreground py-24 px-4 md:px-8 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#222_1px,transparent_1px),linear-gradient(to_bottom,#222_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-20 pointer-events-none" />

      <div ref={ref} className={`max-w-7xl mx-auto transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
        {}
        <div className="mb-16 text-center">
          <h2 className="font-serif text-4xl mb-4 tracking-tight !text-[hsl(50,98%,64%)] md:text-5xl lg:text-6xl">
            Performance Arsenal
          </h2>
          <p className="font-sans text-muted-foreground text-sm uppercase tracking-[0.2em]">
            Technical Rider & Equipment Specification
          </p>
          <p className="font-sans text-muted-foreground text-sm uppercase tracking-[0.2em]">Featuring the AZ sound system for high-fidelity audio reinforcement.</p>
        </div>

        {}
        <div className="border border-white grid grid-cols-1 lg:grid-cols-3 bg-black/50 backdrop-blur-sm">
          {equipment.map((item, i) => <div key={i} data-index={i} className={`
                group relative p-8 md:p-12 flex flex-col h-full
                border-b lg:border-b-0 border-white/20 lg:border-white
                ${i !== equipment.length - 1 ? "lg:border-r" : ""}
                hover:bg-white/5 transition-colors duration-500
              `}>
              {}
              <div className="flex justify-between items-start mb-8 border-b border-white/20 pb-4">
                <div className="font-mono text-xs text-primary tracking-widest">
                  0{i + 1} 
                </div>
                <div className="opacity-50 group-hover:opacity-100 transition-opacity duration-300">
                  {item.icon}
                </div>
              </div>

              {}
              <div className="relative aspect-square mb-8 bg-white/5 border border-white/10 p-6 flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.1)_0%,transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <img src={item.image} alt={item.model} className="w-full h-full object-contain grayscale group-hover:grayscale-0 transition-all duration-500 scale-90 group-hover:scale-100" />
                
                {}
                <div className="absolute top-2 left-2 w-2 h-2 border-t border-l border-white/30" />
                <div className="absolute top-2 right-2 w-2 h-2 border-t border-r border-white/30" />
                <div className="absolute bottom-2 left-2 w-2 h-2 border-b border-l border-white/30" />
                <div className="absolute bottom-2 right-2 w-2 h-2 border-b border-r border-white/30" />
              </div>

              {}
              <div className="mt-auto">
                <h3 className="font-serif text-2xl text-white mb-2">
                  {item.model}
                </h3>
                <div className="font-mono text-xs uppercase tracking-wider mb-4 !text-[hsl(50,98%,64%)]">
                  {item.title}
                </div>
                
                <p className="font-sans text-sm text-gray-400 leading-relaxed mb-6 border-l-2 border-primary/50 pl-4">
                  {item.description}
                </p>

                {}
                <ul className="space-y-2 font-mono text-xs text-gray-500">
                  {item.specs.map((spec, idx) => <li key={idx} className="flex items-center gap-2">
                      <span className="w-1 h-1 bg-primary rounded-full" />
                      <span className="uppercase">{spec}</span>
                    </li>)}
                </ul>
              </div>
            </div>)}
        </div>

        {}
        <div className="mt-12 flex justify-center">
          
        </div>
      </div>
    </section>;
}